/**
 * DetailPanel - Right-side panel showing:
 *   - A multi-select summary when items are checked
 *   - The full NotificationDetail when one item is selected
 *   - An empty-state prompt otherwise
 */
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { NotificationDetail } from './NotificationDetail';
import type { Notification } from '../../api/notificationApi';

interface DetailPanelProps {
  selectedItem: Notification | undefined;
  checkedCount: number;
  onMarkAsUnread: () => void;
  onDelete: () => void;
  onLabelClick: (label: string) => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
}

export function DetailPanel({
  selectedItem,
  checkedCount,
  onMarkAsUnread,
  onDelete,
  onLabelClick,
  hasActiveFilters,
  onClearFilters,
}: DetailPanelProps) {
  if (checkedCount > 0 && !hasActiveFilters) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          gap: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" color="text.primary">
          {checkedCount} item{checkedCount !== 1 ? 's' : ''} selected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Use the toolbar on the left to mark as read, unread, or clear your
          selection.
        </Typography>
      </Box>
    );
  }

  if (selectedItem) {
    return (
      <NotificationDetail
        notification={selectedItem}
        onMarkAsUnread={onMarkAsUnread}
        onDelete={onDelete}
        onLabelClick={onLabelClick}
      />
    );
  }

  if (hasActiveFilters) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          gap: 1,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" color="text.primary">
          No exact results found.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try changing or removing some filters.
        </Typography>
        {onClearFilters && (
          <Button
            size="small"
            variant="outlined"
            onClick={onClearFilters}
            sx={{ mt: 1, textTransform: 'none' }}
          >
            Clear all
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        gap: 2,
        textAlign: 'center',
      }}
    >
      <Typography variant="h6" color="text.primary">
        Select an item to see details.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        You can also organize your board based on your priorities.
      </Typography>
    </Box>
  );
}
